const clamp = (value, min, max) =>
    Math.min(Math.max(value, min), max);


// ======================================================
// CARRIÈRE SIGNIFICATIVE — /25
// ======================================================

export const calculateCareerScore = (credits = []) => {
    if (!Array.isArray(credits) || !credits.length) {
        return 0;
    }

    const significantMovies = credits.filter(
        (movie) =>
            movie?.id &&
            movie?.release_date &&
            Number(movie.vote_count || 0) >= 250
    );

    const strongMovies = credits.filter(
        (movie) =>
            movie?.id &&
            Number(movie.vote_count || 0) >= 1000
    );

    const significantCount = significantMovies.length;
    const strongCount = strongMovies.length;

    // 15 points : ampleur réelle de carrière.
    // Il faut maintenant environ 50 films significatifs
    // pour atteindre entièrement cette partie.
    const volumePart =
        clamp(significantCount / 50, 0, 1) * 15;

    // 10 points : densité qualitative.
    // Quelle proportion des films significatifs
    // atteint aussi le niveau 1000+ ?
    const strongRatio =
        significantCount > 0
            ? strongCount / significantCount
            : 0;

    const qualityPart =
        clamp(strongRatio / 0.8, 0, 1) * 10;

    return Math.round(
        clamp(volumePart + qualityPart, 0, 25)
    );
};


// ======================================================
// IMPACT DES FILMS — /30
// ======================================================

export const calculateMovieImpactScore = (credits = []) => {
    if (!Array.isArray(credits) || !credits.length) {
        return 0;
    }

    const significantMovies = credits.filter(
        (movie) =>
            movie?.id &&
            movie?.release_date &&
            Number(movie.vote_count || 0) >= 250
    );

    const strongMovies = credits.filter(
        (movie) =>
            movie?.id &&
            Number(movie.vote_count || 0) >= 1000
    );

    const majorMovies = credits.filter(
        (movie) =>
            movie?.id &&
            Number(movie.vote_count || 0) >= 5000
    );

    const significantCount = significantMovies.length;
    const strongCount = strongMovies.length;
    const majorCount = majorMovies.length;

    // 12 points : quantité de films forts.
    const strongPart =
        clamp(strongCount / 30, 0, 1) * 12;

    // 10 points : quantité de très gros films.
    const majorPart =
        clamp(majorCount / 15, 0, 1) * 10;

    // 8 points : concentration de très gros succès
    // dans la filmographie significative.
    const majorRatio =
        significantCount > 0
            ? majorCount / significantCount
            : 0;

    const majorDensityPart =
        clamp(majorRatio / 0.35, 0, 1) * 8;

    return Math.round(
        clamp(
            strongPart +
            majorPart +
            majorDensityPart,
            0,
            30
        )
    );
};

// ======================================================
// CURRENT HEAT — /25
// ======================================================

export const calculateCurrentHeatScore = (credits = []) => {
    if (!Array.isArray(credits) || !credits.length) {
        return 0;
    }

    const currentYear = new Date().getFullYear();

    const recentCredits = credits.filter((movie) => {
        const releaseYear =
            Number(movie?.release_date?.slice(0, 4));

        return (
            Number.isFinite(releaseYear) &&
            releaseYear >= currentYear - 4 &&
            releaseYear <= currentYear
        );
    });

    const recentSignificantMovies =
        recentCredits.filter(
            (movie) =>
                Number(movie.vote_count || 0) >= 250
        );

    const recentStrongMovies =
        recentCredits.filter(
            (movie) =>
                Number(movie.vote_count || 0) >= 1000
        );

    const recentMajorMovies =
        recentCredits.filter(
            (movie) =>
                Number(movie.vote_count || 0) >= 5000
        );

    const significantCount =
        recentSignificantMovies.length;

    const strongCount =
        recentStrongMovies.length;

    const majorCount =
        recentMajorMovies.length;

    // 10 points :
    // présence récente dans des films réellement forts.
    const strongPart =
        clamp(strongCount / 3, 0, 1) * 10;

    // 8 points :
    // très gros succès récents.
    const majorPart =
        clamp(majorCount / 2, 0, 1) * 8;

    // 5 points :
    // densité des films forts parmi les films significatifs.
    const strongDensity =
        significantCount > 0
            ? strongCount / significantCount
            : 0;

    const densityPart =
        clamp(strongDensity, 0, 1) * 5;

    // 2 points :
    // activité significative récente.
    const activityPart =
        clamp(significantCount / 4, 0, 1) * 2;

    return Math.round(
        clamp(
            strongPart +
            majorPart +
            densityPart +
            activityPart,
            0,
            25
        )
    );
};

// ======================================================
// ÉVALUATION SUPERSTAR — RÉALISATEURS V1
// ======================================================

export const calculateDirectorSuperStarScore = ({
    credits = [],
    prestige = 0,
    legacy = false,
    editorialOverride = false,
    regionalIcon = false,
} = {}) => {
    const currentYear = new Date().getFullYear();

    const seenMovieIds = new Set();

    /*
     * On accepte :
     * - des crédits déjà filtrés par ActeurDetails;
     * - ou des crédits TMDB possédant encore leur propriété job.
     */
    const directorCredits = (
        Array.isArray(credits) ? credits : []
    )
        .filter((movie) => {
            if (!movie?.id) {
                return false;
            }

            const job = String(
                movie.job || ""
            ).toLowerCase();

            return !job || job === "director";
        })
        .filter((movie) => {
            const movieId = Number(movie.id);

            if (seenMovieIds.has(movieId)) {
                return false;
            }

            seenMovieIds.add(movieId);
            return true;
        });

    const datedCredits = directorCredits.filter(
        (movie) => {
            const releaseYear = Number(
                movie?.release_date?.slice(0, 4)
            );

            return (
                Number.isFinite(releaseYear) &&
                releaseYear >= 1888 &&
                releaseYear <= currentYear
            );
        }
    );

    const recognizedMovies = datedCredits.filter(
        (movie) =>
            Number(movie.vote_count || 0) >= 100
    );

    const significantMovies = datedCredits.filter(
        (movie) =>
            Number(movie.vote_count || 0) >= 250
    );

    const strongMovies = datedCredits.filter(
        (movie) =>
            Number(movie.vote_count || 0) >= 1000
    );

    const majorMovies = datedCredits.filter(
        (movie) =>
            Number(movie.vote_count || 0) >= 5000
    );

    const releaseYears = recognizedMovies
        .map((movie) =>
            Number(movie.release_date.slice(0, 4))
        )
        .sort((a, b) => a - b);

    const careerSpanYears =
        releaseYears.length >= 2
            ? releaseYears[releaseYears.length - 1] -
            releaseYears[0]
            : 0;

    // ------------------------------------------------------
    // CARRIÈRE — /25
    // ------------------------------------------------------

    const careerVolumePart =
        clamp(
            significantMovies.length / 15,
            0,
            1
        ) * 12;

    const careerLongevityPart =
        clamp(
            careerSpanYears / 30,
            0,
            1
        ) * 8;

    const strongMovieRatio =
        significantMovies.length > 0
            ? strongMovies.length /
            significantMovies.length
            : 0;

    const careerQualityPart =
        clamp(
            strongMovieRatio / 0.6,
            0,
            1
        ) * 5;

    const careerScore = Math.round(
        clamp(
            careerVolumePart +
            careerLongevityPart +
            careerQualityPart,
            0,
            25
        )
    );

    // ------------------------------------------------------
    // IMPACT DE LA FILMOGRAPHIE — /30
    // ------------------------------------------------------

    const strongImpactPart =
        clamp(
            strongMovies.length / 10,
            0,
            1
        ) * 12;

    const majorImpactPart =
        clamp(
            majorMovies.length / 5,
            0,
            1
        ) * 12;

    const majorMovieRatio =
        significantMovies.length > 0
            ? majorMovies.length /
            significantMovies.length
            : 0;

    const impactDensityPart =
        clamp(
            majorMovieRatio / 0.35,
            0,
            1
        ) * 6;

    const movieImpactScore = Math.round(
        clamp(
            strongImpactPart +
            majorImpactPart +
            impactDensityPart,
            0,
            30
        )
    );

    /*
     * Ce calcul repose seulement sur la date et
     * l’impact des films : il est donc partageable
     * avec le moteur des acteurs.
     */
    const currentHeatScore =
        calculateCurrentHeatScore(directorCredits);

    const prestigeScore = Math.round(
        clamp(
            Number(prestige) || 0,
            0,
            20
        )
    );

    const automaticSubtotal =
        careerScore + movieImpactScore;

    const automaticTotal =
        automaticSubtotal + currentHeatScore;

    const finalScore =
        automaticTotal + prestigeScore;

    const automaticQualified =
        automaticTotal >= 55;

    const scoreQualified =
        automaticQualified ||
        finalScore >= 70;

    /*
     * Un réalisateur peut aussi être reconnu grâce
     * à une longue carrière ou à une signature forte,
     * même si son volume est inférieur à celui
     * d’un acteur très prolifique.
     */
    const iconQualified = Boolean(
        !scoreQualified &&
        careerSpanYears >= 35 &&
        significantMovies.length >= 15 &&
        majorMovies.length >= 5
    );

    const signatureQualified = Boolean(
        !scoreQualified &&
        !iconQualified &&
        careerSpanYears >= 10 &&
        strongMovies.length >= 5 &&
        majorMovies.length >= 3
    );

    const regionalIconQualified = Boolean(
        regionalIcon &&
        !scoreQualified &&
        !iconQualified &&
        !signatureQualified &&
        recognizedMovies.length >= 8 &&
        careerSpanYears >= 10
    );

    const legacyQualified = Boolean(
        legacy &&
        !scoreQualified &&
        !iconQualified &&
        prestigeScore >= 18
    );

    const editorialQualified =
        Boolean(editorialOverride);

    const recommendedSuperStar = Boolean(
        scoreQualified ||
        iconQualified ||
        signatureQualified ||
        regionalIconQualified ||
        legacyQualified ||
        editorialQualified
    );

    const qualificationReason =
        editorialQualified
            ? "editorial"
            : legacyQualified
                ? "legacy"
                : scoreQualified
                    ? "score"
                    : iconQualified
                        ? "director-icon"
                        : signatureQualified
                            ? "director-signature"
                            : regionalIconQualified
                                ? "regional-icon"
                                : "none";

    const recentCredits = datedCredits.filter(
        (movie) => {
            const releaseYear = Number(
                movie.release_date.slice(0, 4)
            );

            return releaseYear >= currentYear - 4;
        }
    );

    return {
        role: "director",

        careerScore,
        movieImpactScore,
        currentHeatScore,
        prestigeScore,

        automaticSubtotal,
        automaticTotal,
        finalScore,

        automaticQualified,
        scoreQualified,
        iconQualified,
        signatureQualified,
        culturalIconQualified: false,
        regionalIconQualified,
        legacyQualified,
        editorialQualified,
        recommendedSuperStar,
        qualificationReason,

        diagnostics: {
            totalCredits: directorCredits.length,
            datedCreditsCount: datedCredits.length,
            recognizedMovieCount:
                recognizedMovies.length,
            significantMovieCount:
                significantMovies.length,
            strongMovieCount:
                strongMovies.length,
            majorMovieCount:
                majorMovies.length,
            recentCreditsCount:
                recentCredits.length,
            careerSpanYears,
        },
    };
};

// ======================================================
// ÉVALUATION AUTOMATIQUE V2
// ======================================================

export const calculateSuperStarAutoScore = ({
    credits = [],
    prestige = 0,
    legacy = false,
    editorialOverride = false,
    regionalIcon = false,
} = {}) => {
    const careerScore =
        calculateCareerScore(credits);

    const movieImpactScore =
        calculateMovieImpactScore(credits);

    const currentHeatScore =
        calculateCurrentHeatScore(credits);

    const prestigeScore =
        Math.round(
            clamp(Number(prestige) || 0, 0, 20)
        );

    // ------------------------------------------------------
    // DIAGNOSTICS DE CALIBRATION
    //
    // Ces valeurs n'ajoutent encore aucun point.
    // Elles servent seulement à comprendre pourquoi
    // un acteur obtient son score actuel.
    // ------------------------------------------------------

    const significantMovieCount = credits.filter(
        (movie) =>
            movie?.id &&
            movie?.release_date &&
            Number(movie.vote_count || 0) >= 250
    ).length;

    const strongMovieCount = credits.filter(
        (movie) =>
            movie?.id &&
            Number(movie.vote_count || 0) >= 1000
    ).length;

    const majorMovieCount = credits.filter(
        (movie) =>
            movie?.id &&
            Number(movie.vote_count || 0) >= 5000
    ).length;

    // ------------------------------------------------------
    // CURRENT HEAT — DIAGNOSTICS
    // Fenêtre glissante : année actuelle + 4 années précédentes.
    // Aucun point attribué pour l'instant.
    // ------------------------------------------------------

    const currentYear = new Date().getFullYear();

    const recentCredits = credits.filter((movie) => {
        const releaseYear =
            Number(movie?.release_date?.slice(0, 4));

        return (
            Number.isFinite(releaseYear) &&
            releaseYear >= currentYear - 4 &&
            releaseYear <= currentYear
        );
    });

    const recentSignificantMovieCount =
        recentCredits.filter(
            (movie) =>
                Number(movie.vote_count || 0) >= 250
        ).length;

    const recentStrongMovieCount =
        recentCredits.filter(
            (movie) =>
                Number(movie.vote_count || 0) >= 1000
        ).length;

    const recentMajorMovieCount =
        recentCredits.filter(
            (movie) =>
                Number(movie.vote_count || 0) >= 5000
        ).length;

    const automaticTotal =
        careerScore +
        movieImpactScore +
        currentHeatScore;

    const finalScore =
        automaticTotal + prestigeScore;

    const automaticQualified =
        automaticTotal >= 55;

    const scoreQualified =
        automaticQualified ||
        finalScore >= 70;

    const legacyQualified =
        Boolean(
            legacy &&
            !scoreQualified &&
            prestigeScore >= 18
        );

    const editorialQualified =
        Boolean(editorialOverride);

    const datedCredits = credits.filter((movie) => {
        const releaseDate = movie?.release_date;

        const characterLabel =
            String(movie?.character || "").toLowerCase();

        const isArchiveCredit =
            characterLabel.includes("archive footage") ||
            characterLabel.includes("archival footage");

        if (isArchiveCredit) {
            return false;
        }

        if (
            typeof releaseDate !== "string" ||
            releaseDate.length < 4
        ) {
            return false;
        }

        const year = Number(
            releaseDate.slice(0, 4)
        );

        return (
            Number.isFinite(year) &&
            year >= 1888 &&
            year <= currentYear
        );
    });

    const careerSpanCredits = datedCredits.filter(
        (movie) =>
            Number.isFinite(Number(movie?.order)) &&
            Number(movie.order) <= 2 &&
            Number(movie?.vote_count || 0) >= 250
    );

    const releaseYears = careerSpanCredits
        .map((movie) =>
            Number(movie.release_date.slice(0, 4))
        )
        .sort((a, b) => a - b);

    const careerSpanYears =
        releaseYears.length >= 2
            ? releaseYears[releaseYears.length - 1] -
            releaseYears[0]
            : 0;

    const topBilledCredits = credits.filter(
        (movie) =>
            Number.isFinite(Number(movie?.order)) &&
            Number(movie.order) <= 2
    );

    const topBilledRecognizedCount =
        topBilledCredits.filter(
            (movie) =>
                Number(movie.vote_count || 0) >= 100
        ).length;

    const topBilledSignificantCount =
        topBilledCredits.filter(
            (movie) =>
                Number(movie.vote_count || 0) >= 250
        ).length;

    const leadKnownCredits = credits.filter(
        (movie) =>
            Number.isFinite(Number(movie?.order)) &&
            Number(movie.order) <= 1 &&
            Number(movie?.vote_count || 0) >= 100
    );

    const leadSignificantCredits = credits.filter(
        (movie) =>
            Number.isFinite(Number(movie?.order)) &&
            Number(movie.order) <= 1 &&
            Number(movie?.vote_count || 0) >= 250
    );

    const leadStrongCredits = credits.filter(
        (movie) =>
            Number.isFinite(Number(movie?.order)) &&
            Number(movie.order) <= 1 &&
            Number(movie?.vote_count || 0) >= 1000
    );

    const leadMajorCredits = credits.filter(
        (movie) =>
            Number.isFinite(Number(movie?.order)) &&
            Number(movie.order) <= 1 &&
            Number(movie?.vote_count || 0) >= 5000
    );

    const leadSignificantDensity =
        leadKnownCredits.length > 0
            ? leadSignificantCredits.length /
            leadKnownCredits.length
            : 0;

    const leadStrongDensity =
        leadSignificantCredits.length > 0
            ? leadStrongCredits.length /
            leadSignificantCredits.length
            : 0;

    const iconQualified =
        Boolean(
            !scoreQualified &&
            careerSpanYears >= 45 &&
            topBilledCredits.length >= 35 &&
            topBilledRecognizedCount >= 20 &&
            topBilledSignificantCount >= 12
        );

    const signatureQualified =
        Boolean(
            !scoreQualified &&
            !iconQualified &&
            careerSpanYears >= 10 &&
            leadMajorCredits.length >= 3 &&
            leadStrongCredits.length >= 3
        );

    const culturalIconQualified =
        Boolean(
            !scoreQualified &&
            !iconQualified &&
            !signatureQualified &&
            careerSpanYears >= 6 &&
            leadKnownCredits.length >= 8 &&
            leadSignificantCredits.length >= 5 &&
            leadStrongCredits.length >= 3 &&
            leadSignificantDensity >= 0.5 &&
            leadStrongDensity >= 0.45
        );

    const regionalIconQualified =
        Boolean(
            regionalIcon &&
            credits.length >= 25 &&
            topBilledCredits.length >= 10
        );

    const recommendedSuperStar =
        Boolean(
            scoreQualified ||
            iconQualified ||
            signatureQualified ||
            culturalIconQualified ||
            regionalIconQualified ||
            legacyQualified ||
            editorialQualified
        );

    const qualificationReason =
        editorialQualified
            ? "editorial"
            : legacyQualified
                ? "legacy"
                : scoreQualified
                    ? "score"
                    : iconQualified
                        ? "icon"
                        : signatureQualified
                            ? "signature"
                            : culturalIconQualified
                                ? "cultural-icon"
                                : regionalIconQualified
                                    ? "regional-icon"
                                    : "none";

    return {
        careerScore,
        movieImpactScore,
        currentHeatScore,
        prestigeScore,

        // Carrière + impact uniquement.
        automaticSubtotal:
            careerScore + movieImpactScore,

        // Partie entièrement automatique actuelle.
        automaticTotal,
        finalScore,
        automaticQualified,
        scoreQualified,
        iconQualified,
        signatureQualified,
        culturalIconQualified,
        regionalIconQualified,
        legacyQualified,
        editorialQualified,
        recommendedSuperStar,
        qualificationReason,

        diagnostics: {
            totalCredits: credits.length,
            significantMovieCount,
            strongMovieCount,
            majorMovieCount,
            recentCreditsCount: recentCredits.length,
            recentSignificantMovieCount,
            recentStrongMovieCount,
            recentMajorMovieCount,
            careerSpanYears,
            topBilledCount: topBilledCredits.length,
            topBilledRecognizedCount,
            topBilledSignificantCount,
            leadKnownCount: leadKnownCredits.length,
            leadSignificantCount: leadSignificantCredits.length,
            leadStrongCount: leadStrongCredits.length,
            leadMajorCount: leadMajorCredits.length,
            leadSignificantDensity,
            leadStrongDensity,
        },
    };
};